import React, { useState, useEffect } from "react";
import { getMeetingTopics } from "../../../services/api/safetymeeting";
import { formatDate } from "../../../utils/datetime";
import { Spinner } from "../../../components/ui";
import { ChevronDown, ChevronRight } from "../../../components/icons/svg/outline";

/**
 * Safety Meetings Dashboard
 *
 * Displays all weekly safety meeting topics organized by year and month
 * Shows topic, crew assignments, and worker counts for each week
 */
const SafetyMeeting = () => {
  document.title = "R&S Safety Meetings";

  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Get current year and month for default expansion
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-11

  useEffect(() => {
    /**
     * Loads safety-meeting topics and expands the current year/month.
     *
     * @returns {Promise<void>}
     */
    const fetchTopics = async () => {
      try {
        setIsLoading(true);
        const data = await getMeetingTopics();
        setTopics(data);
        setError(null);

        // Auto-expand current year and month
        setExpandedYears(new Set([currentYear]));
        setExpandedMonths(new Set([`${currentYear}-${currentMonth}`]));
      } catch (err) {
        console.error("Error fetching safety meeting topics:", err);
        setError("Failed to load safety meeting data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopics();
  }, [currentYear, currentMonth]);

  // Group topics by year and month
  /**
   * Groups topics by calendar year then month from `week_ending`.
   *
   * @param {Array<Object>} topics Topic rows.
   * @returns {Object} Nested `{[year]: {[month]: topics}}` map.
   */
  const groupTopicsByYearAndMonth = (topics) => {
    const grouped = {};

    topics.forEach((topic) => {
      const date = new Date(topic.week_ending);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11

      if (!grouped[year]) {
        grouped[year] = {};
      }
      if (!grouped[year][month]) {
        grouped[year][month] = [];
      }
      grouped[year][month].push(topic);
    });

    return grouped;
  };

  const groupedTopics = groupTopicsByYearAndMonth(topics);
  const years = Object.keys(groupedTopics).sort((a, b) => b - a); // Descending order

  /**
   * Toggles a week accordion open or closed.
   *
   * @param {string} weekEnding Week-ending date key.
   * @returns {void}
   */
  const toggleWeekExpanded = (weekEnding) => {
    setExpandedWeeks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(weekEnding)) {
        newSet.delete(weekEnding);
      } else {
        newSet.add(weekEnding);
      }
      return newSet;
    });
  };

  /**
   * Toggles a year accordion open or closed.
   *
   * @param {number} year Calendar year.
   * @returns {void}
   */
  const toggleYearExpanded = (year) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  /**
   * Toggles a year-month accordion open or closed.
   *
   * @param {number} year Calendar year.
   * @param {number} month Month index 0–11.
   * @returns {void}
   */
  const toggleMonthExpanded = (year, month) => {
    const key = `${year}-${month}`;
    setExpandedMonths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  /**
   * Returns the English month name for a 0–11 index.
   *
   * @param {number} monthIndex Month index.
   * @returns {string} Month name.
   */
  const getMonthName = (monthIndex) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[monthIndex];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-2 max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-lg text-center">
          <p className="text-red-700 font-semibold mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-custom-rsbg">
      <div className="container mx-auto px-4 py-2 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg mb-2">
          <div className="bg-green-900 text-white p-4 rounded-t-lg">
            <h1 className="text-2xl font-bold">Weekly Safety Meetings</h1>
            <p className="text-green-100 text-sm mt-1">
              View safety topics and crew assignments by week
            </p>
          </div>

          {/* Summary Stats */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex gap-6">
              <div>
                <span className="text-gray-600 text-sm">Total Weeks:</span>
                <span className="ml-2 font-semibold">{topics.length}</span>
              </div>
              <div>
                <span className="text-gray-600 text-sm">With Crew Data:</span>
                <span className="ml-2 font-semibold">
                  {topics.filter((t) => t.crew_data).length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Topics List Grouped by Year and Month */}
        {topics.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Safety Meeting Topics Yet
            </h3>
            <p className="text-gray-500">
              Safety meeting topics will appear here once they are generated.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {years.map((year) => {
              const yearNum = parseInt(year);
              const isYearExpanded = expandedYears.has(yearNum);
              const months = Object.keys(groupedTopics[year]).sort((a, b) => b - a);
              const yearTopicCount = months.reduce((sum, month) =>
                sum + groupedTopics[year][month].length, 0
              );

              return (
                <div key={year} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Year Header */}
                  <div
                    className="bg-green-800 text-white p-4 cursor-pointer hover:bg-green-700 transition-colors flex items-center justify-between"
                    onClick={() => toggleYearExpanded(yearNum)}
                  >
                    <div className="flex items-center gap-3">
                      {isYearExpanded ? (
                        <ChevronDown className="w-6 h-6" />
                      ) : (
                        <ChevronRight className="w-6 h-6" />
                      )}
                      <h2 className="text-xl font-bold">{year}</h2>
                      <span className="text-green-100 text-sm">
                        ({yearTopicCount} week{yearTopicCount !== 1 ? "s" : ""})
                      </span>
                    </div>
                  </div>

                  {/* Months */}
                  {isYearExpanded && (
                    <div className="p-2 space-y-2">
                      {months.map((month) => {
                        const monthNum = parseInt(month);
                        const monthKey = `${year}-${month}`;
                        const isMonthExpanded = expandedMonths.has(monthKey);
                        const monthTopics = groupedTopics[year][month];

                        return (
                          <div key={monthKey} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Month Header */}
                            <div
                              className="bg-gray-100 p-3 cursor-pointer hover:bg-gray-200 transition-colors flex items-center justify-between"
                              onClick={() => toggleMonthExpanded(yearNum, monthNum)}
                            >
                              <div className="flex items-center gap-3">
                                {isMonthExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-600" />
                                )}
                                <h3 className="text-lg font-semibold text-gray-800">
                                  {getMonthName(monthNum)}
                                </h3>
                                <span className="text-gray-600 text-sm">
                                  ({monthTopics.length} week{monthTopics.length !== 1 ? "s" : ""})
                                </span>
                              </div>
                            </div>

                            {/* Week Topics */}
                            {isMonthExpanded && (
                              <div className="p-3 space-y-2 bg-gray-50">
                                {monthTopics.map((topic) => {
                                  const isExpanded = expandedWeeks.has(topic.week_ending);
                                  const hasCrew = topic.crew_data && topic.crew_data.crews;

                                  return (
                                    <div
                                      key={topic.id}
                                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                                    >
                                      {/* Topic Header - Always Visible */}
                                      <div
                                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                        onClick={() => toggleWeekExpanded(topic.week_ending)}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                              <h4 className="text-base font-semibold text-gray-900">
                                                Week Ending:{" "}
                                                {formatDate(topic.week_ending, "month DD, YYYY")}
                                              </h4>
                                              {hasCrew ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                                                  ✓ Generated
                                                </span>
                                              ) : (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                                                  Not Generated
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-gray-700 font-medium text-sm">
                                              {topic.topic_display}
                                            </p>
                                            {hasCrew && (
                                              <div className="flex gap-4 mt-2 text-xs text-gray-600">
                                                <span>
                                                  {topic.crew_data.total_crews} crew
                                                  {topic.crew_data.total_crews !== 1 ? "s" : ""}
                                                </span>
                                                <span>•</span>
                                                <span>
                                                  {topic.crew_data.total_workers} worker
                                                  {topic.crew_data.total_workers !== 1 ? "s" : ""}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          <div className="ml-4">
                                            {isExpanded ? (
                                              <ChevronDown className="w-5 h-5 text-gray-400" />
                                            ) : (
                                              <ChevronRight className="w-5 h-5 text-gray-400" />
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Expanded Crew Details */}
                                      {isExpanded && hasCrew && (
                                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                                          <h5 className="font-semibold text-gray-700 mb-3 text-sm">
                                            Crew Assignments
                                          </h5>
                                          <div className="space-y-2">
                                            {topic.crew_data.crews.map((crew, idx) => (
                                              <div
                                                key={idx}
                                                className="bg-white rounded border border-gray-200 p-3"
                                              >
                                                <div className="flex items-start justify-between mb-2">
                                                  <div>
                                                    <h6 className="font-semibold text-gray-800 text-sm">
                                                      {crew.job_number} - {crew.job_description}
                                                    </h6>
                                                    <p className="text-xs text-gray-600">
                                                      {crew.address}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                      Date: {formatDate(crew.date, "month DD, YYYY")}
                                                    </p>
                                                  </div>
                                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                                    {crew.worker_count} worker
                                                    {crew.worker_count !== 1 ? "s" : ""}
                                                  </span>
                                                </div>

                                                {crew.foreman && (
                                                  <div className="mb-2">
                                                    <span className="text-xs font-semibold text-gray-700">
                                                      Foreman:
                                                    </span>
                                                    <span className="text-xs text-gray-600 ml-2">
                                                      {crew.foreman.name}
                                                    </span>
                                                  </div>
                                                )}

                                                <div>
                                                  <span className="text-xs font-semibold text-gray-700">
                                                    Workers:
                                                  </span>
                                                  <div className="mt-1 flex flex-wrap gap-2">
                                                    {crew.workers.map((worker) => (
                                                      <span
                                                        key={worker.id}
                                                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                                      >
                                                        {worker.name}
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* No Crew Data State */}
                                      {isExpanded && !hasCrew && (
                                        <div className="border-t border-gray-200 bg-gray-50 p-4 text-center text-gray-500 text-sm">
                                          Crew assignments have not been generated for this week.
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyMeeting;
